import { defineStore } from 'pinia'
import backendHandler from '@/BackendAPI.js'
import { useLogsStore } from '@/store/logs'
import { useInstanceStore } from '@/store/backendInstances.js'
import DisplayModeSyncable from '@/syncing/syncables/DisplayModeSyncable.js'
import SyncableHandler from '@/syncing/SyncableHandler.js'
import { displayModeSchema, parseDisplayModeAndRemoveIllegalProperties } from '@/composables/displayModeSchema'
import { DisplayMode, DisplayModeProperty, PropertySources } from '@/types/DisplayMode'

export const useDisplayModeStore = defineStore('displayMode', {
  state: () => ({
    displayModes: {} as Record<string, DisplayMode>,
    propertySources: {} as Record<string, PropertySources>,
    canDoCloudOverrides: {} as Record<string, boolean>,
    instanceDefaults: {} as Record<string, Partial<DisplayMode>>,
  }),
  persist: true,
  share: {
    enable: true,
    initialize: true,
  },
  getters: {
    getDisplayMode() {
      return (globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string): DisplayMode => {
        return this.displayModes[globalUniqueIdentifier]
      }
    },
    getPropertySources() {
      return (globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string): PropertySources => {
        return this.propertySources[globalUniqueIdentifier] || ({} as PropertySources)
      }
    },
    canDoCloudOverride() {
      return (globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string) => {
        return this.canDoCloudOverrides[globalUniqueIdentifier] || false
      }
    },
    getInstanceDefaults() {
      return (globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string): Partial<DisplayMode> => {
        return this.instanceDefaults[globalUniqueIdentifier] || {}
      }
    },
  },
  actions: {
    async queryOneLocalOverrideDisplayModeSettings(payload: object | undefined, globalUniqueIdentifier = undefined, abortSignal = undefined) {
      if (!globalUniqueIdentifier) globalUniqueIdentifier = useInstanceStore().selectedInstanceId
      const displayModeResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/display-mode/settings/local-override/queryOne',
        data: payload,
        signal: abortSignal,
      })
      if (!displayModeResponse.ok) return undefined
      return displayModeResponse.data
    },
    async queryOneCloudDisplayModeSettings(payload: object | undefined, globalUniqueIdentifier = undefined, abortSignal = undefined) {
      if (!globalUniqueIdentifier) globalUniqueIdentifier = useInstanceStore().selectedInstanceId
      const displayModeResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/display-mode/settings/cloud/queryOne',
        data: payload,
        signal: abortSignal,
      })
      if (!displayModeResponse.ok) return undefined
      return displayModeResponse.data
    },
    async queryCloudDisplayModeSettings(payload: object | undefined, globalUniqueIdentifier = undefined, abortSignal = undefined) {
      if (!globalUniqueIdentifier) globalUniqueIdentifier = useInstanceStore().selectedInstanceId
      const displayModeResponse = await backendHandler.post(globalUniqueIdentifier, {
        path: '/api/display-mode/settings/cloud/query',
        data: payload,
        signal: abortSignal,
      })
      if (!displayModeResponse.ok) return null
      return displayModeResponse.data
    },
    async updateSpecificProperty(
      property: DisplayModeProperty,
      value: unknown,
      cloud: boolean = false,
      globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string,
      accountType?: string,
      accountId?: string
    ) {
      let parsedValue = null
      const validator = displayModeSchema.shape[property]
      if (value !== null) {
        if (!validator) throw new Error(`Property ${property} is not a valid display mode property.`)
        const parseResult = validator.safeParse(value)
        if (!parseResult.success) {
          useLogsStore().addLogEntry({
            message: `Failed to parse display mode property ${property}: ${parseResult.error.message}`,
            tag: 'displayModeStore',
            level: 'ERROR',
            globalUniqueIdentifier: globalUniqueIdentifier,
          })
          return false
        }
        parsedValue = parseResult.data
      }

      let path = `/api/display-mode/settings/${cloud ? 'cloud-override' : 'local-override'}/`
      if (accountType && accountId) {
        path += `${accountType}/${accountId}`
      }
      //@ts-expect-error backendhandler isn't TS and the globalUniqueIdentifier defaults to undefined which breaks type inference
      await backendHandler.patch(globalUniqueIdentifier, {
        path: path,
        data: {
          patches: {
            [property]: parsedValue,
          },
        },
      })
      if (!accountType && !accountId) {
        await this.loadDisplayMode(globalUniqueIdentifier)
      }
      return true
    },

    async loadDisplayMode(globalUniqueIdentifier: string = useInstanceStore().selectedInstanceId as string) {
      //@ts-expect-error backendhandler isn't TS and the globalUniqueIdentifier defaults to undefined which breaks type inference
      const displayModeResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/display-mode/settings',
        requiresAuth: true,
      })
      if (!displayModeResponse.ok) {
        useLogsStore().addLogEntry({
          message: 'Failed to load display mode settings',
          tag: 'displayModeStore',
          level: 'ERROR',
          globalUniqueIdentifier: globalUniqueIdentifier,
          error: displayModeResponse.error,
          httpMethod: 'GET',
        })
        return
      }
      this.displayModes[globalUniqueIdentifier] = parseDisplayModeAndRemoveIllegalProperties(
        displayModeResponse.data.settings,
        globalUniqueIdentifier
      )
      this.propertySources[globalUniqueIdentifier] = displayModeResponse.data.propertySources
      this.canDoCloudOverrides[globalUniqueIdentifier] =
        !displayModeResponse.data.errors || !Object.keys(displayModeResponse.data.errors).includes('cloudOverride')
      return this.displayModes[globalUniqueIdentifier]
    },
    async loadInstanceDefaults(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      const instanceDefaultsResponse = await backendHandler.get(globalUniqueIdentifier, {
        path: '/api/display-mode/settings/defaults',
        requiresAuth: true,
      })
      if (instanceDefaultsResponse.ok) this.instanceDefaults[globalUniqueIdentifier] = instanceDefaultsResponse.data
      return instanceDefaultsResponse.ok
    },
    async ensureForInstance(globalUniqueIdentifier = useInstanceStore().selectedInstanceId) {
      await SyncableHandler.ensureSyncable(DisplayModeSyncable, globalUniqueIdentifier)
    },
  },
})
